<template>
  <div class="kanban-board">
    <woot-loading-state
      v-if="isFetching"
      :message="$t('OPPORTUNITY_KANBAN.LOADING')"
    />
    <empty-state
      v-else-if="!boardColumns.length"
      :title="$t('OPPORTUNITY_KANBAN.EMPTY_TITLE')"
      :message="$t('OPPORTUNITY_KANBAN.EMPTY_MESSAGE')"
    />
    <div v-else class="kanban-board__columns">
      <div
        v-for="column in boardColumns"
        :key="column.stage.id || 'unassigned'"
        class="kanban-board__column"
        :class="{
          'kanban-board__column--inactive': column.stage.status === 'inactive',
        }"
        :style="{ '--column-color': column.stage.color || '#94a3b8' }"
      >
        <header class="kanban-board__column-header">
          <span class="kanban-board__column-dot" />
          <span class="kanban-board__column-name">{{ column.stage.name }}</span>
          <span class="kanban-board__column-count">
            {{
              $t('OPPORTUNITY_KANBAN.COLUMN_COUNT', {
                loaded: column.cards.length,
                total: column.totalCount,
              })
            }}
          </span>
        </header>

        <draggable
          ref="columnBody"
          v-model="column.cards"
          class="kanban-board__column-body"
          group="opportunity-kanban"
          :sort="false"
          :disabled="!canWriteOpportunities"
          :move="checkMove"
          animation="150"
          ghost-class="kanban-card--ghost"
          chosen-class="kanban-card--dragging"
          @change="event => onColumnChange(event, column)"
          @scroll.native="onColumnScroll(column, $event)"
        >
          <opportunity-kanban-card
            v-for="opportunity in column.cards"
            :key="opportunity.id"
            :opportunity="opportunity"
            :disabled="isCardBlocked(opportunity.id)"
            @navigate-start="onNavigateStart"
            @navigate-cancel="onNavigateCancel"
          />
        </draggable>

        <footer
          v-if="
            (hasMore(column) && column.isNearBottom) || column.isLoadingMore
          "
          class="kanban-board__column-footer"
        >
          <woot-button
            v-if="!column.isLoadingMore"
            variant="clear"
            size="expanded"
            @click="loadMore(column)"
          >
            {{ $t('OPPORTUNITY_KANBAN.LOAD_MORE') }}
          </woot-button>
          <spinner v-else size="small" />
        </footer>
      </div>
    </div>
    <opportunity-negotiation-drawer
      v-if="negotiationMove"
      :show="!!negotiationMove"
      :opportunity="negotiationMove.opportunity"
      :target-stage="negotiationMove.stage"
      :pending-question-ids="negotiationMove.pendingQuestionIds"
      @saved="completeNegotiationMove"
      @close="closeNegotiationMove"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import alertMixin from 'shared/mixins/alertMixin';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';
import Spinner from 'shared/components/Spinner';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import EmptyState from 'dashboard/components/widgets/EmptyState';
import { buildOpportunityFilterParams } from 'dashboard/helper/opportunityKanbanHelper';
import {
  hasActiveNegotiationForm,
  pendingQuestionIdsFromError,
} from 'dashboard/helper/opportunityNegotiationFormHelper';
import OpportunityKanbanCard from './OpportunityKanbanCard.vue';
import OpportunityNegotiationDrawer from '../negotiationForm/OpportunityNegotiationDrawer.vue';

const SCROLL_BOTTOM_THRESHOLD = 100;

export default {
  components: {
    draggable,
    EmptyState,
    OpportunityKanbanCard,
    OpportunityNegotiationDrawer,
    Spinner,
  },
  mixins: [alertMixin, opportunityWritePermission],
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
    pipelineLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      boardColumns: [],
      navigatingOpportunityId: null,
      negotiationMove: null,
    };
  },
  computed: {
    ...mapGetters({ pipelines: 'opportunityPipelines/getPipelines' }),
  },
  watch: {
    columns: 'rebuildColumns',
  },
  mounted() {
    this.rebuildColumns();
  },
  methods: {
    rebuildColumns() {
      this.boardColumns = this.columns.map(column => ({
        stage: column.stage || this.unassignedStageMeta(),
        cards: [...column.opportunities],
        totalCount: column.total_count,
        page: 1,
        isLoadingMore: false,
        isNearBottom: false,
      }));
      this.$nextTick(this.refreshAllNearBottom);
    },
    columnBodyEl(index) {
      const refs = this.$refs.columnBody;
      const ref = Array.isArray(refs) ? refs[index] : refs;
      return ref && ref.$el ? ref.$el : ref;
    },
    updateNearBottom(column, index) {
      const el = this.columnBodyEl(index);
      if (!el) return;

      column.isNearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        SCROLL_BOTTOM_THRESHOLD;
    },
    refreshAllNearBottom() {
      this.boardColumns.forEach((column, index) =>
        this.updateNearBottom(column, index)
      );
    },
    onColumnScroll(column, event) {
      const el = event.target;
      column.isNearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        SCROLL_BOTTOM_THRESHOLD;
    },
    unassignedStageMeta() {
      return {
        id: null,
        name: this.$t('OPPORTUNITY_KANBAN.UNASSIGNED_STAGE'),
        color: '#94a3b8',
        status: 'inactive',
      };
    },
    hasMore(column) {
      return column.cards.length < column.totalCount;
    },
    async loadMore(column) {
      if (column.isLoadingMore || !this.hasMore(column)) return;

      column.isLoadingMore = true;
      try {
        const nextPage = column.page + 1;
        const opportunities = await this.$store.dispatch('opportunities/list', {
          ...buildOpportunityFilterParams(this.filters),
          pipeline_stage_id:
            column.stage.id === null ? 'unassigned' : column.stage.id,
          sort_by: 'updated_at',
          page: nextPage,
        });

        column.cards.push(...opportunities);
        column.page = nextPage;
        const index = this.boardColumns.indexOf(column);
        this.$nextTick(() => this.updateNearBottom(column, index));
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_KANBAN.LOAD_MORE_ERROR')
        );
      } finally {
        column.isLoadingMore = false;
      }
    },
    isOpportunityLocked(opportunity) {
      return opportunity.status !== 'open' || this.pipelineLocked;
    },
    isCardBlocked(opportunityId) {
      return (
        this.navigatingOpportunityId !== null &&
        this.navigatingOpportunityId !== opportunityId
      );
    },
    onNavigateStart(opportunityId) {
      this.navigatingOpportunityId = opportunityId;
    },
    onNavigateCancel() {
      this.navigatingOpportunityId = null;
    },
    checkMove(event) {
      if (this.pipelineLocked || !this.canWriteOpportunities) return false;

      const opportunity = event.draggedContext.element;
      if (this.isOpportunityLocked(opportunity)) return false;

      const targetList = event.relatedContext.list;
      const targetColumn = this.boardColumns.find(
        column => column.cards === targetList
      );
      if (!targetColumn) return true;

      return (
        targetColumn.stage.id !== null &&
        targetColumn.stage.status !== 'inactive'
      );
    },
    async onColumnChange(event, column) {
      if (event.removed) {
        column.totalCount = Math.max(0, column.totalCount - 1);
      }
      if (!event.added) return;

      column.totalCount += 1;
      const { element: opportunity } = event.added;
      const negotiationMove = await this.pendingNegotiationMove(
        opportunity,
        column.stage
      );
      if (negotiationMove) {
        this.$emit('refresh');
        this.negotiationMove = negotiationMove;
        return;
      }
      try {
        await this.$store.dispatch('opportunities/moveStage', {
          opportunityId: opportunity.id,
          pipelineStageId: column.stage.id,
        });
        opportunity.pipeline_stage = {
          id: column.stage.id,
          name: column.stage.name,
          position: column.stage.position,
        };
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_KANBAN.MOVE_ERROR')
        );
        this.$emit('refresh');
        this.openNegotiationMove(
          opportunity.id,
          column.stage,
          pendingQuestionIdsFromError(error)
        );
      }
    },
    hasActiveNegotiationFormFor(pipelineId) {
      const pipeline = (this.pipelines || []).find(
        item => Number(item.id) === Number(pipelineId)
      );
      return hasActiveNegotiationForm(pipeline);
    },
    async pendingNegotiationMove(card, stage) {
      if (!this.hasActiveNegotiationFormFor(card.pipeline_id)) return null;

      try {
        const opportunity = await this.$store.dispatch(
          'opportunities/getById',
          card.id
        );
        const pendingQuestionIds = await this.$store.dispatch(
          'opportunities/pendingNegotiationQuestionIds',
          { opportunity, stage }
        );
        return pendingQuestionIds && pendingQuestionIds.length
          ? { opportunity, stage, pendingQuestionIds }
          : null;
      } catch (error) {
        return null;
      }
    },
    async openNegotiationMove(opportunityId, stage, pendingQuestionIds) {
      if (!pendingQuestionIds.length) return;

      const opportunity = await this.$store
        .dispatch('opportunities/getById', opportunityId)
        .catch(() => null);
      if (!opportunity) return;

      this.negotiationMove = { opportunity, stage, pendingQuestionIds };
    },
    closeNegotiationMove() {
      this.negotiationMove = null;
    },
    async completeNegotiationMove(opportunity) {
      const { stage } = this.negotiationMove;
      try {
        await this.$store.dispatch('opportunities/moveStage', {
          opportunityId: opportunity.id,
          pipelineStageId: stage.id,
        });
        this.negotiationMove = null;
        this.showAlert(
          this.$t('OPPORTUNITY_KANBAN.MOVED', { stageName: stage.name })
        );
      } catch (error) {
        const pendingQuestionIds = pendingQuestionIdsFromError(error);
        this.negotiationMove = pendingQuestionIds.length
          ? { opportunity, stage, pendingQuestionIds }
          : null;
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_KANBAN.MOVE_ERROR')
        );
      } finally {
        this.$emit('refresh');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.kanban-board {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.kanban-board__columns {
  display: flex;
  flex: 1;
  gap: var(--space-normal);
  min-height: 0;
  overflow-x: auto;
}

.kanban-board__column {
  background: var(--s-25);
  border-radius: var(--border-radius-medium);
  display: flex;
  flex-direction: column;
  flex: 1 1 260px;
  max-height: 100%;
  max-width: 400px;
  min-width: 260px;

  &--inactive {
    opacity: 0.7;
  }
}

.kanban-board__column-header {
  align-items: center;
  border-top: 4px solid var(--column-color);
  border-radius: var(--border-radius-medium) var(--border-radius-medium) 0 0;
  display: flex;
  gap: var(--space-smaller);
  padding: var(--space-small) var(--space-normal);
}

.kanban-board__column-dot {
  background: var(--column-color);
  border-radius: 50%;
  height: 10px;
  width: 10px;
}

.kanban-board__column-name {
  flex: 1;
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kanban-board__column-count {
  color: var(--s-500);
  flex-shrink: 0;
  font-size: var(--font-size-mini);
}

.kanban-board__column-body {
  flex: 1;
  min-height: 4rem;
  overflow-y: auto;
  padding: 0 var(--space-small) var(--space-small);
}

.kanban-board__column-footer {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0 var(--space-small) var(--space-small);
}
</style>
